import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useDownloadCSV, useGetEmployerIdParam } from '../../hooks';
import { CIPStatusReportTableParams } from '../CIPStatusReport.types';

export const QUERY_KEY = 'EXPORT_CIP_STATUS_REPORT_CSV';
const useCardStatusReportExportCSVQuery = (params: CIPStatusReportTableParams) => {
  const handleSaveFile = useDownloadCSV();
  const [isLoading, setIsLoading] = useState(false);

  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);
  const getEmployerIdParam = useGetEmployerIdParam();
  const employerId = getEmployerIdParam(params.employerId, params.employerIds);
  const { refetch } = useQuery(
    [QUERY_KEY],
    () =>
      api.get(API_URL.REPORTS_CIP_STATUS_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: _.pickBy({
          ...employerId,
          show_ssn: params.isSSNShowed,
          cip_status_start_date: formattedStartDate,
          cip_status_end_date: formattedEndDate,
          cip_statuses:
            params?.CIPStatuses && !params?.CIPStatuses.includes(ALL_OPTION_KEY)
              ? params?.CIPStatuses.join(',')
              : undefined,
          reason_types:
            params?.reason && !params?.reason.includes(ALL_OPTION_KEY) ? params?.reason.join(',') : undefined,

          enrollment_statuses:
            params?.enrollmentStatuses && !params?.enrollmentStatuses.includes(ALL_OPTION_KEY)
              ? params?.enrollmentStatuses.join(',')
              : undefined,
          employment_statuses:
            params?.employmentStatuses && !params?.employmentStatuses.includes(ALL_OPTION_KEY)
              ? params?.employmentStatuses.join(',')
              : undefined,
          account_statuses:
            params?.accountStatuses && !params?.accountStatuses.includes(ALL_OPTION_KEY)
              ? params?.accountStatuses.join(',')
              : undefined,

          search_value: params.searchString,
        }),
      }),
    {
      retry: 0,
      enabled: false,
    }
  );

  const handleExport = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await refetch();
      handleSaveFile(response?.data?.data, 'csv', 'cip-status_report');
    } finally {
      setIsLoading(false);
    }
  }, [handleSaveFile, refetch]);
  return {
    isLoading,
    handleExport,
  };
};

export default useCardStatusReportExportCSVQuery;
